import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";

import { useConfigContext } from "shared/contexts/ConfigContext";

import { INGESTION_INFO_PAGE_CONFIG_KEY } from "pages/SuperAdmin/PagesConfig/types";

import Button from "features/components/Button";
import Popover from "features/components/Popover";
import Typography from "features/components/Typography";

import { INGESTION_TODAY, IngestionEntry } from "./constants";
import { useIngestionEntries } from "./hooks";
import IngestionInfoTable from "./IngestionInfoTable";
import { getButtonDateLabel, getIngestionDateLabel } from "./utils";

const ELEMENT_ID = "ingestion-info-action";

const IngestionInfo = () => {
  const { t } = useTranslation("common");
  const { pages } = useConfigContext();
  const { entries: data, failed } = useIngestionEntries();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  if (!pages[INGESTION_INFO_PAGE_CONFIG_KEY] || failed) {
    return null;
  }

  if (!data) {
    return (
      <Button
        data-testid="open-ingestion-info-action"
        size="small"
        color="primary"
        startIcon={<EventNoteOutlinedIcon sx={{ fontSize: 20 }} />}
      >
        <div className="line-clamp-2">{t("ingestion.loading")}</div>
      </Button>
    );
  }

  const ingestionEntriesWithDates: IngestionEntry[] = data.filter(
    ({ ingestedAt }) => ingestedAt
  ) as IngestionEntry[];

  // Find the oldest ingestion entry
  const oldestIngestionEntry: IngestionEntry | null =
    ingestionEntriesWithDates.length > 0
      ? ingestionEntriesWithDates.reduce((oldest, current) =>
          current.ingestedAt!.dateTimestamp < oldest.ingestedAt!.dateTimestamp
            ? current
            : oldest
        )
      : null;

  return (
    <>
      <Button
        aria-owns={open ? "mouse-over-popover" : undefined}
        aria-haspopup="true"
        onClick={handlePopoverOpen}
        data-testid="open-ingestion-info-action"
        size="small"
        disabled={data.length === 0}
        color="primary"
        startIcon={<EventNoteOutlinedIcon sx={{ fontSize: 20 }} />}
      >
        <div className="line-clamp-2">
          {oldestIngestionEntry
            ? oldestIngestionEntry.ingestedAt?.dateLabel &&
              t("ingestion.data_updated", {
                date: getButtonDateLabel(oldestIngestionEntry.ingestedAt, t),
              })
            : t("ingestion.no_data_received")}
        </div>
      </Button>
      <Popover
        id={ELEMENT_ID}
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        onClose={handlePopoverClose}
        disableRestoreFocus
      >
        <Box className="w-[26rem]">
          <Stack className="p-4" spacing={1} direction="column">
            <Typography variant="h4">{t("ingestion.data_status")}</Typography>
            {oldestIngestionEntry && oldestIngestionEntry?.ingestedAt && (
              <Typography variant="body2">
                <Trans
                  t={t}
                  i18nKey="ingestion.received"
                  values={{
                    date: getIngestionDateLabel(
                      oldestIngestionEntry.ingestedAt.dateLabel,
                      t
                    ),
                  }}
                  components={{ b: <b /> }}
                />
                {oldestIngestionEntry &&
                  oldestIngestionEntry?.ingestedAt.dateLabel !==
                    INGESTION_TODAY && (
                    <>
                      <br />
                      {t("ingestion.some_recent")}
                    </>
                  )}
              </Typography>
            )}
            {data.length > 0 ? (
              <IngestionInfoTable data={data} />
            ) : (
              <Typography variant="body2">
                {t("ingestion.no_updates")}
              </Typography>
            )}
          </Stack>
        </Box>
      </Popover>
    </>
  );
};

export default IngestionInfo;
